import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "./client";

export interface CreateOrderField {
  key: string;
  value: string | number | boolean;
}

export interface CreateOrderRequest {
  service_id: number;
  custom_id: string;
  fields: CreateOrderField[];
}

export interface CreateOrderResponse {
  custom_id: string;
  total_to_pay: string;
  status: string;
}

export interface PayOrderResponse {
  custom_id: string;
  status: "completed" | "refunded" | "insufficient" | "in_progress";
  balance: string;
  pins?: unknown[] | null;
  note?: string | null;
}

export interface OrderInfoResponse {
  custom_id: string;
  status: number;
  status_message: string;
  product?: string | null;
  quantity?: number | null;
  total_price?: number | null;
  date?: string | null;
  pins?: unknown[] | null;
  data?: unknown;
}

export interface OrderListItem {
  custom_id: string;
  service_id: number | null;
  product: string | null;
  quantity: number | null;
  total_price: number | null;
  status: number;
  status_message: string;
  date: string | null;
  /** Steam-topup recipient login lifted from `ns_orders.data.account`.
   *  When set, the row is a Steam top-up and the SPA labels it
   *  accordingly instead of dumping the bare login into the product
   *  column. Null for code purchases / steam_gift / etc. */
  account_id: string | null;
}

export interface OrderListResponse {
  items: OrderListItem[];
  total: number;
  offset: number;
  limit: number;
}

export function newUuid(): string {
  // Native crypto.randomUUID is available in modern browsers and avoids
  // pulling a uuid lib for one call site.
  return crypto.randomUUID();
}

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation<CreateOrderResponse, ApiError, CreateOrderRequest>({
    mutationFn: (body) =>
      api.post<CreateOrderResponse>("/web/api/orders/create", { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export interface PayOrderRequest {
  custom_id: string;
  totp_code?: string;
}

export function usePayOrder() {
  const qc = useQueryClient();
  return useMutation<PayOrderResponse, ApiError, PayOrderRequest>({
    mutationFn: (body) =>
      api.post<PayOrderResponse>("/web/api/orders/pay", { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["balance"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

/** Pay endpoint returns 428 with `{detail: {requires_totp: true}}`
 * when the user has 2FA + require_purchase. SPA branches on this. */
export function isPayRequiresTotp(err: unknown): boolean {
  if (!(err instanceof Error) || !("status" in err) || !("detail" in err)) {
    return false;
  }
  const e = err as { status: unknown; detail: unknown };
  if (e.status !== 428) return false;
  return (
    typeof e.detail === "object" &&
    e.detail !== null &&
    (e.detail as { requires_totp?: unknown }).requires_totp === true
  );
}

export function useOrderInfo(customId: string | null) {
  return useQuery<OrderInfoResponse, ApiError>({
    queryKey: ["order_info", customId],
    queryFn: () =>
      api.get<OrderInfoResponse>(
        `/web/api/orders/${encodeURIComponent(customId!)}`,
      ),
    enabled: !!customId,
    refetchInterval: (q) => {
      const data = q.state.data as OrderInfoResponse | undefined;
      // Poll while the order is still in flight (V2 status 10 = in_progress).
      return data && data.status === 10 ? 3_000 : false;
    },
  });
}

export function useOrders(params: {
  offset?: number;
  limit?: number;
  status?: number;
  service_id?: number;
  custom_id?: string;
  account_id?: string;
  date_from?: string;
  date_to?: string;
} = {}) {
  const qs = new URLSearchParams();
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.status !== undefined) qs.set("status", String(params.status));
  if (params.service_id !== undefined)
    qs.set("service_id", String(params.service_id));
  if (params.custom_id) qs.set("custom_id", params.custom_id);
  if (params.account_id) qs.set("account_id", params.account_id);
  if (params.date_from) qs.set("date_from", params.date_from);
  if (params.date_to) qs.set("date_to", params.date_to);
  const search = qs.toString();
  const path = `/web/api/orders${search ? "?" + search : ""}`;
  return useQuery<OrderListResponse, ApiError>({
    queryKey: ["orders", params],
    queryFn: () => api.get<OrderListResponse>(path),
  });
}
